/**
 * T065/T066 (001-catalogue-admin-screens, US3). Composes `tier-price-row.tsx`'s cells into
 * `ColumnDef`s the same way `catalogue/brands/columns.tsx` composes the domain `Badge` inline —
 * see that file's own header comment for the template this list's shape (name/id, code, status,
 * actions) is copied from. No arithmetic here either: every cell renders a field the API returned.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { Text } from "rizzui";
import {
  PiArrowCounterClockwiseDuotone as RevertIcon,
  PiCaretDownBold as CaretDown,
  PiCaretUpBold as CaretUp,
  PiBarcodeDuotone as SkuIcon,
} from "react-icons/pi";
import { RowActions } from "../../../components/table/row-actions";
import { TierPriceOrigin, TierPriceRateCell, TierPriceStaleBadge } from "../../../components/tier-price-row";
import type { TierPriceRow } from "../../../lib/services/catalog";
import { skuPath } from "../../../lib/paths";

export function tierPriceColumns(opts: {
  canWrite: boolean;
  expandedId: string | null;
  onToggle: (row: TierPriceRow) => void;
  onRevert: (row: TierPriceRow) => void;
}): ColumnDef<TierPriceRow>[] {
  const { canWrite, expandedId, onToggle, onRevert } = opts;

  return [
    { id: "product", accessorKey: "productName", header: "Product", size: 220, cell: ({ row }) => row.original.productName },
    {
      id: "sku",
      accessorKey: "skuCode",
      header: "SKU",
      size: 160,
      cell: ({ row }) => (
        <Link to={skuPath(row.original.skuId)} className="font-mono text-xs hover:underline">
          {row.original.skuCode}
        </Link>
      ),
    },
    { id: "unit", accessorKey: "unitCode", header: "Unit", size: 100, cell: ({ row }) => row.original.unitCode },
    {
      id: "base",
      // The base rate this row was calculated from, as the API recorded it (`derivedFromBaseRate`).
      accessorFn: (price) => (price.derivedFromBaseRate == null ? undefined : Number(price.derivedFromBaseRate)),
      sortUndefined: "last",
      header: "Base rate",
      size: 130,
      meta: { align: "right" },
      cell: ({ row }) => (
        <span className="tabular-nums text-gray-500">
          <TierPriceRateCell rate={row.original.derivedFromBaseRate} />
        </span>
      ),
    },
    {
      id: "rate",
      // The API's own rate read as a number, so the sort compares amounts. Shown as sent.
      accessorFn: (price) => Number(price.rate),
      header: "Tier rate",
      size: 140,
      meta: { align: "right" },
      cell: ({ row }) => (
        <span className="tabular-nums">
          <TierPriceRateCell rate={row.original.rate} />
        </span>
      ),
    },
    { id: "origin", accessorKey: "source", header: "Origin", size: 150, cell: ({ row }) => <TierPriceOrigin source={row.original.source} /> },
    { id: "stale", accessorKey: "isStale", header: "Staleness", size: 130, cell: ({ row }) => <TierPriceStaleBadge isStale={row.original.isStale} /> },
    {
      id: "gst",
      accessorKey: "gstTreatment",
      header: "GST",
      size: 110,
      cell: ({ row }) => <Text className="text-xs text-gray-500 uppercase">{row.original.gstTreatment}</Text>,
    },
    {
      id: "actions",
      header: "",
      size: 90,
      // The row's one ⋮ menu: open or close its rule-attribution panel (and rate editor), or go to the SKU.
      cell: ({ row }) => {
        const isOpen = expandedId === row.original.id;
        const label = isOpen ? "Hide rule attribution" : canWrite ? "View and edit this rate" : "View rule attribution";
        return (
          <RowActions
            name={`${row.original.skuCode} ${row.original.unitCode}`}
            primary={{ label, icon: isOpen ? CaretUp : CaretDown, onClick: () => onToggle(row.original) }}
            more={[
              { label: "Open the SKU", icon: SkuIcon, to: skuPath(row.original.skuId) },
              // ASPL-10159: the one way a hand-set row goes back under the tier's rules.
              ...(canWrite && row.original.source === "manually_set"
                ? [{ label: "Revert to rule", icon: RevertIcon, onClick: () => onRevert(row.original) }]
                : []),
            ]}
          />
        );
      },
    },
  ];
}
